import type { Event, Task, Supplier, Message, Suggestion, ChatMessage, DataSourceStatus } from '../types/mockup'

export const mockSuppliers: Supplier[] = [
  {
    id: 'sup-1',
    name: 'Elite Catering Co.',
    role: 'Catering',
    contactEmail: 'events@elitecatering.com',
    contactPhone: '+1-555-0101',
    color: '#FF6B6B',
    status: 'on_site',
    lastContact: new Date('2024-12-15T13:30:00')
  },
  {
    id: 'sup-2',
    name: 'Bloom & Blossom Florals',
    role: 'Florist',
    contactEmail: 'sarah@bloomblossom.com',
    contactPhone: '+1-555-0102',
    color: '#4ECDC4',
    status: 'on_site',
    lastContact: new Date('2024-12-15T12:00:00')
  },
  {
    id: 'sup-3',
    name: 'Capture Moments Photography',
    role: 'Photographer',
    contactEmail: 'booking@capturemoments.com',
    contactPhone: '+1-555-0103',
    color: '#45B7D1',
    status: 'on_site',
    lastContact: new Date('2024-12-15T14:00:00')
  },
  {
    id: 'sup-4',
    name: 'The Jazz Quartet',
    role: 'Band/Music',
    contactEmail: 'manager@jazzquartet.com',
    contactPhone: '+1-555-0104',
    color: '#96CEB4',
    status: 'on_site',
    lastContact: new Date('2024-12-15T14:20:00')
  },
  {
    id: 'sup-5',
    name: 'Elegant Events Decor',
    role: 'Decorator',
    contactEmail: 'team@elegantevents.com',
    contactPhone: '+1-555-0105',
    color: '#FFEAA7',
    status: 'on_site',
    lastContact: new Date('2024-12-15T11:00:00')
  },
  {
    id: 'sup-6',
    name: 'Premier AV Solutions',
    role: 'Audio/Visual',
    contactEmail: 'tech@premierav.com',
    contactPhone: '+1-555-0106',
    color: '#DDA0DD',
    status: 'on_site',
    lastContact: new Date('2024-12-15T13:00:00')
  },
  {
    id: 'sup-7',
    name: 'Sweet Dreams Bakery',
    role: 'Wedding Cake',
    contactEmail: 'orders@sweetdreamsbakery.com',
    contactPhone: '+1-555-0107',
    color: '#FFB6C1',
    status: 'confirmed',
    lastContact: new Date('2024-12-15T10:00:00')
  },
  {
    id: 'sup-8',
    name: 'Ritz Carlton Events Team',
    role: 'Venue Staff',
    contactEmail: 'events@ritzcarlton.com',
    contactPhone: '+1-555-0100',
    color: '#B19CD9',
    status: 'on_site',
    lastContact: new Date('2024-12-15T08:00:00')
  }
]

const baseDate = new Date('2024-12-15T00:00:00')
const createTime = (hours: number, minutes: number) => {
  const date = new Date(baseDate)
  date.setHours(hours, minutes, 0, 0)
  return date
}

export const mockTasks: Task[] = [
  {
    id: 'task-1',
    name: 'Venue Opens - Staff Arrival',
    startTime: createTime(9, 0),
    endTime: createTime(9, 15),
    duration: 15,
    supplierId: 'sup-8',
    supplier: mockSuppliers[7],
    status: 'complete',
    assignedTo: ['Venue Coordinator'],
    location: 'Main Entrance',
    isAnchor: true,
    priority: 'critical',
    dependencies: [],
    dependents: ['task-2'],
    notes: 'Security check, staff briefing',
    buffer: 0
  },
  {
    id: 'task-2',
    name: 'Linen Placement on Tables',
    startTime: createTime(9, 30),
    endTime: createTime(10, 0),
    duration: 30,
    supplierId: 'sup-8',
    supplier: mockSuppliers[7],
    status: 'complete',
    assignedTo: ['Setup Team'],
    location: 'Grand Ballroom',
    isAnchor: false,
    priority: 'high',
    dependencies: ['task-1'],
    dependents: ['task-3', 'task-4'],
    notes: 'White tablecloths, napkins, chair covers',
    buffer: 15
  },
  {
    id: 'task-3',
    name: 'AV Team Arrives & Setup',
    startTime: createTime(10, 0),
    endTime: createTime(11, 0),
    duration: 60,
    supplierId: 'sup-6',
    supplier: mockSuppliers[5],
    status: 'complete',
    assignedTo: ['AV Technicians'],
    location: 'Grand Ballroom',
    isAnchor: false,
    priority: 'high',
    dependencies: ['task-2'],
    dependents: ['task-8'],
    notes: 'Sound system, microphones, projectors, lighting',
    buffer: 30
  },
  {
    id: 'task-4',
    name: 'Florist Arrives - Centerpieces',
    startTime: createTime(10, 30),
    endTime: createTime(11, 30),
    duration: 60,
    supplierId: 'sup-2',
    supplier: mockSuppliers[1],
    status: 'complete',
    assignedTo: ['Florist Team'],
    location: 'Grand Ballroom',
    isAnchor: false,
    priority: 'high',
    dependencies: ['task-2'],
    dependents: [],
    notes: 'Table centerpieces, entrance arrangements',
    buffer: 15
  },
  {
    id: 'task-5',
    name: 'Catering Team Arrives - Kitchen Setup',
    startTime: createTime(11, 0),
    endTime: createTime(12, 0),
    duration: 60,
    supplierId: 'sup-1',
    supplier: mockSuppliers[0],
    status: 'complete',
    assignedTo: ['Catering Manager'],
    location: 'Kitchen',
    isAnchor: false,
    priority: 'high',
    dependencies: [],
    dependents: ['task-9'],
    notes: 'Food prep, station setup',
    buffer: 30
  },
  {
    id: 'task-6',
    name: 'Wedding Cake Delivery',
    startTime: createTime(11, 30),
    endTime: createTime(12, 0),
    duration: 30,
    supplierId: 'sup-7',
    supplier: mockSuppliers[6],
    status: 'complete',
    assignedTo: ['Bakery Team'],
    location: 'Reception Hall',
    isAnchor: false,
    priority: 'medium',
    dependencies: [],
    dependents: [],
    notes: '4-tier cake, setup on display table',
    buffer: 15
  },
  {
    id: 'task-7',
    name: 'Photographer Arrives - Pre-ceremony',
    startTime: createTime(12, 0),
    endTime: createTime(13, 0),
    duration: 60,
    supplierId: 'sup-3',
    supplier: mockSuppliers[2],
    status: 'complete',
    assignedTo: ['Photography Team'],
    location: 'Bridal Suite',
    isAnchor: false,
    priority: 'high',
    dependencies: [],
    dependents: ['task-10'],
    notes: 'Bridal party photos, getting ready shots',
    buffer: 15
  },
  {
    id: 'task-8',
    name: 'Band Sound Check',
    startTime: createTime(13, 0),
    endTime: createTime(13, 30),
    duration: 30,
    supplierId: 'sup-4',
    supplier: mockSuppliers[3],
    status: 'complete',
    assignedTo: ['Band Leader'],
    location: 'Grand Ballroom',
    isAnchor: false,
    priority: 'medium',
    dependencies: ['task-3'],
    dependents: [],
    notes: 'Test all instruments and mics',
    buffer: 15
  },
  {
    id: 'task-9',
    name: 'Guest Arrival - Welcome Drinks',
    startTime: createTime(14, 30),
    endTime: createTime(15, 0),
    duration: 30,
    supplierId: 'sup-1',
    supplier: mockSuppliers[0],
    status: 'in_progress',
    assignedTo: ['Catering Team'],
    location: 'Garden Terrace',
    isAnchor: false,
    priority: 'high',
    dependencies: ['task-5'],
    dependents: ['task-10'],
    notes: 'Welcome cocktails and canapés',
    buffer: 15,
    delayMinutes: 10
  },
  {
    id: 'task-10',
    name: 'Wedding Ceremony',
    startTime: createTime(15, 30),
    endTime: createTime(16, 30),
    duration: 60,
    supplierId: 'sup-8',
    supplier: mockSuppliers[7],
    status: 'pending',
    assignedTo: ['Wedding Coordinator'],
    location: 'Grand Ballroom',
    isAnchor: true,
    priority: 'critical',
    dependencies: ['task-7', 'task-9'],
    dependents: ['task-11'],
    notes: 'Main ceremony event',
    buffer: 0
  },
  {
    id: 'task-11',
    name: 'Cocktail Hour',
    startTime: createTime(16, 30),
    endTime: createTime(17, 30),
    duration: 60,
    supplierId: 'sup-1',
    supplier: mockSuppliers[0],
    status: 'pending',
    assignedTo: ['Catering Team', 'Bar Staff'],
    location: 'Garden Terrace',
    isAnchor: false,
    priority: 'high',
    dependencies: ['task-10'],
    dependents: ['task-12'],
    notes: 'Drinks and hors d\'oeuvres',
    buffer: 15
  },
  {
    id: 'task-12',
    name: 'Reception Dinner',
    startTime: createTime(18, 0),
    endTime: createTime(20, 0),
    duration: 120,
    supplierId: 'sup-1',
    supplier: mockSuppliers[0],
    status: 'pending',
    assignedTo: ['Catering Team'],
    location: 'Grand Ballroom',
    isAnchor: true,
    priority: 'critical',
    dependencies: ['task-11'],
    dependents: ['task-13'],
    notes: '3-course dinner service',
    buffer: 0
  },
  {
    id: 'task-13',
    name: 'First Dance & Party',
    startTime: createTime(20, 0),
    endTime: createTime(23, 0),
    duration: 180,
    supplierId: 'sup-4',
    supplier: mockSuppliers[3],
    status: 'pending',
    assignedTo: ['Band', 'DJ'],
    location: 'Grand Ballroom',
    isAnchor: false,
    priority: 'medium',
    dependencies: ['task-12'],
    dependents: [],
    notes: 'Dancing and entertainment',
    buffer: 30
  }
]

export const mockWeddingEvent: Event = {
  id: 'event-001',
  name: 'Sarah & James Wedding',
  date: new Date('2024-12-15'),
  venue: 'Grand Ballroom, The Ritz Carlton',
  status: 'active',
  tasks: mockTasks,
  suppliers: mockSuppliers,
  messages: [],
  suggestions: []
}

export const mockMessages: Message[] = [
  {
    id: 'msg-1',
    from: 'Elite Catering Co.',
    source: 'sms',
    content: 'Running 10 minutes late due to traffic on highway. ETA 2:40 PM for cocktail setup.',
    timestamp: new Date('2024-12-15T14:28:00'),
    isProcessed: false,
    messageUnderstandingScore: 0.92,
    parsedIntent: {
      action: 'delay',
      targetTask: 'Guest Arrival - Welcome Drinks',
      duration: 10,
      messageUnderstandingScore: 0.92
    }
  },
  {
    id: 'msg-2',
    from: 'Photographer',
    source: 'whatsapp',
    content: 'On site and ready. Setting up in the garden for pre-ceremony shots.',
    timestamp: new Date('2024-12-15T12:00:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.88,
    parsedIntent: {
      action: 'confirm',
      targetTask: 'Photographer Arrives',
      messageUnderstandingScore: 0.88
    }
  },
  {
    id: 'msg-3',
    from: 'Band Leader',
    source: 'email',
    content: 'Sound check complete. All systems working perfectly. Ready for ceremony.',
    timestamp: new Date('2024-12-15T13:30:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.95,
    parsedIntent: {
      action: 'complete',
      targetTask: 'Band Sound Check',
      messageUnderstandingScore: 0.95
    }
  },
  {
    id: 'msg-4',
    from: 'Florist Team',
    source: 'sms',
    content: 'All centerpieces in place. Ceremony arch looks beautiful!',
    timestamp: new Date('2024-12-15T11:30:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.90,
    parsedIntent: {
      action: 'complete',
      targetTask: 'Florist Arrives - Centerpieces',
      messageUnderstandingScore: 0.90
    }
  },
  {
    id: 'msg-5',
    from: 'Venue Coordinator',
    source: 'opera',
    content: 'VIP guest requires wheelchair access for ceremony. Arranging ramp setup.',
    timestamp: new Date('2024-12-15T14:00:00'),
    isProcessed: false,
    messageUnderstandingScore: 0.75,
    parsedIntent: {
      action: 'note',
      targetTask: 'Wedding Ceremony',
      messageUnderstandingScore: 0.75
    }
  },
  // Additional Email Messages (11 more to reach 12 total)
  {
    id: 'msg-11',
    from: 'Wedding Planner',
    source: 'email',
    content: 'Final headcount confirmed: 142 guests for dinner service. Updated seating chart attached.',
    timestamp: new Date('2024-12-15T09:15:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.94
  },
  {
    id: 'msg-12',
    from: 'Elite Catering Co.',
    source: 'email',
    content: 'Menu confirmation: All dietary restrictions noted. Vegan options prepared for 8 guests.',
    timestamp: new Date('2024-12-15T08:30:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.91
  },
  {
    id: 'msg-13',
    from: 'Premier AV Solutions',
    source: 'email',
    content: 'Equipment delivery complete. Wireless mic system tested and ready. Backup units available.',
    timestamp: new Date('2024-12-15T10:45:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.88
  },
  {
    id: 'msg-14',
    from: 'Sweet Dreams Bakery',
    source: 'email',
    content: 'Wedding cake delivered successfully. Display setup complete in reception area.',
    timestamp: new Date('2024-12-15T12:15:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.92
  },
  {
    id: 'msg-15',
    from: 'Bloom & Blossom Florals',
    source: 'email',
    content: 'Bridal bouquet and boutonnieres ready for pickup. Ceremony arrangements complete.',
    timestamp: new Date('2024-12-15T11:00:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.89
  },
  {
    id: 'msg-16',
    from: 'Ritz Carlton Events',
    source: 'email',
    content: 'Venue setup complete. Security briefing finished. All staff positions confirmed.',
    timestamp: new Date('2024-12-15T09:30:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.95
  },
  {
    id: 'msg-17',
    from: 'The Jazz Quartet',
    source: 'email',
    content: 'Song list confirmed with bride and groom. Special request added for first dance.',
    timestamp: new Date('2024-12-15T10:00:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.93
  },
  {
    id: 'msg-18',
    from: 'Elegant Events Decor',
    source: 'email',
    content: 'Ceremony backdrop installation complete. Lighting effects tested and approved.',
    timestamp: new Date('2024-12-15T11:45:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.87
  },
  {
    id: 'msg-19',
    from: 'Capture Moments Photography',
    source: 'email',
    content: 'Timeline review: Additional 30 minutes requested for family photos. Approved.',
    timestamp: new Date('2024-12-15T08:45:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.90
  },
  {
    id: 'msg-20',
    from: 'Wedding Transportation',
    source: 'email',
    content: 'Bridal party transportation confirmed. Vintage car ready for grand entrance.',
    timestamp: new Date('2024-12-15T09:00:00'),
    isProcessed: false,
    messageUnderstandingScore: 0.86
  },
  {
    id: 'msg-21',
    from: 'Hair & Makeup Studio',
    source: 'email',
    content: 'Bridal party styling complete. Emergency touch-up kit provided for reception.',
    timestamp: new Date('2024-12-15T13:45:00'),
    isProcessed: false,
    messageUnderstandingScore: 0.84
  },
  {
    id: 'msg-22',
    from: 'Officiant',
    source: 'email',
    content: 'Ceremony rehearsal notes reviewed. Special vows approved and ready for reading.',
    timestamp: new Date('2024-12-15T14:10:00'),
    isProcessed: false,
    messageUnderstandingScore: 0.92
  },
  // Additional WhatsApp Messages (8 more to reach 9 total)
  {
    id: 'msg-23',
    from: 'Maid of Honor',
    source: 'whatsapp',
    content: 'Bride is ready! Hair and makeup looks amazing. Heading to ceremony location now.',
    timestamp: new Date('2024-12-15T14:15:00'),
    isProcessed: false,
    messageUnderstandingScore: 0.85
  },
  {
    id: 'msg-24',
    from: 'Best Man',
    source: 'whatsapp',
    content: 'Groom is nervous but excited! Rings secured. We are ready to go.',
    timestamp: new Date('2024-12-15T14:20:00'),
    isProcessed: false,
    messageUnderstandingScore: 0.88
  },
  {
    id: 'msg-25',
    from: 'Mother of Bride',
    source: 'whatsapp',
    content: 'Guest seating is going smoothly. About 80% of guests have arrived so far.',
    timestamp: new Date('2024-12-15T14:25:00'),
    isProcessed: false,
    messageUnderstandingScore: 0.90
  },
  {
    id: 'msg-26',
    from: 'Wedding Coordinator',
    source: 'whatsapp',
    content: 'Weather looking perfect! No rain expected. Outdoor ceremony can proceed as planned.',
    timestamp: new Date('2024-12-15T13:00:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.93
  },
  {
    id: 'msg-27',
    from: 'Venue Security',
    source: 'whatsapp',
    content: 'Parking areas at 90% capacity. Overflow lot is now open for late arrivals.',
    timestamp: new Date('2024-12-15T14:30:00'),
    isProcessed: false,
    messageUnderstandingScore: 0.87
  },
  {
    id: 'msg-28',
    from: 'Catering Assistant',
    source: 'whatsapp',
    content: 'Appetizer station ready. Cocktail hour setup complete in garden area.',
    timestamp: new Date('2024-12-15T13:15:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.89
  },
  {
    id: 'msg-29',
    from: 'Florist Assistant',
    source: 'whatsapp',
    content: 'Last minute touch-ups done. Ceremony aisle petals scattered. Everything perfect!',
    timestamp: new Date('2024-12-15T14:05:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.91
  },
  {
    id: 'msg-30',
    from: 'DJ Setup Crew',
    source: 'whatsapp',
    content: 'Reception music system tested. Dance floor lighting looks amazing! Ready to party!',
    timestamp: new Date('2024-12-15T13:45:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.86
  },
  {
    id: 'msg-31',
    from: 'Guest Relations',
    source: 'whatsapp',
    content: 'Special guest dietary request handled. Kosher meal prepared and labeled for table 8.',
    timestamp: new Date('2024-12-15T12:30:00'),
    isProcessed: true,
    messageUnderstandingScore: 0.88
  }
]

export const mockSuggestions: Suggestion[] = [
  {
    id: 'sug-1',
    type: 'delay',
    title: 'Adjust Cocktail Hour Timing',
    description: 'Catering running 10 minutes late. Suggest pushing Welcome Drinks to 2:40 PM.',
    understanding: 'high',
    source: 'SMS from Elite Catering Co.',
    impact: [
      'Guest Arrival moves to 2:40 PM - 3:10 PM',
      'May affect ceremony start time if not managed'
    ],
    status: 'pending',
    createdAt: new Date('2024-12-15T14:28:00'),
    affectedTasks: ['task-9', 'task-10']
  }
]

export const mockChatMessages: ChatMessage[] = [
  {
    id: 'chat-1',
    type: 'user',
    content: 'Move cocktail hour back by 15 minutes',
    timestamp: new Date('2024-12-15T14:00:00'),
    sender: 'Event Coordinator'
  },
  {
    id: 'chat-2',
    type: 'system',
    content: 'Cocktail hour has been rescheduled to 4:45 PM. All dependent tasks have been adjusted. Notification sent to catering team.',
    timestamp: new Date('2024-12-15T14:00:30'),
    sender: 'Clokd'
  },
  {
    id: 'chat-3',
    type: 'user',
    content: 'Show me the current status of all vendors',
    timestamp: new Date('2024-12-15T14:15:00'),
    sender: 'Event Coordinator'
  },
  {
    id: 'chat-4',
    type: 'system',
    content: 'All vendors are on-site and ready:\n• Catering: On-site (Kitchen)\n• Florist: Complete\n• Photographer: Active (Bridal Suite)\n• Band: Ready\n• AV Team: Complete\n• Venue Staff: Active',
    timestamp: new Date('2024-12-15T14:15:15'),
    sender: 'Clokd'
  }
]

export const mockDataSourceStatus: DataSourceStatus = {
  email: {
    connected: true,
    lastSync: new Date('2024-12-15T14:25:00'),
    messageCount: 24
  },
  sms: {
    connected: true,
    lastSync: new Date('2024-12-15T14:28:00'),
    messageCount: 18
  },
  whatsapp: {
    connected: true,
    lastSync: new Date('2024-12-15T14:20:00'),
    messageCount: 12
  },
  opera: {
    connected: true,
    lastSync: new Date('2024-12-15T14:00:00'),
    eventCount: 1
  },
  amadeus: {
    connected: false,
    lastSync: undefined,
    eventCount: 0
  },
  tripleseat: {
    connected: false,
    lastSync: undefined,
    eventCount: 0
  }
}